import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Shield, UserX, Edit2, Check, Key, Trash2, Eye, EyeOff, UserPlus, MapPin } from 'lucide-react'
import { PageHeader } from '../../components/ui/PageHeader'
import { Modal } from '../../components/ui/Modal'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { AddUserFromStaffModal } from '../../components/settings/AddUserFromStaffModal'
import { UpgradeLimitModal } from '../../components/ui/PlanLimitBar'
import { usePlanLimits, formatLimitLabel } from '../../hooks/usePlanLimits'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { fetchTenantUsers, fetchRoles, fetchWarehouses } from '../../lib/supabaseQueries'
import { supabase } from '../../lib/supabase'
import { useUserLocationAssignments } from '../../hooks/useUserLocationAssignments'
import { useUserLocationIds } from '../../hooks/useUserLocations'
import toast from 'react-hot-toast'
import type { PageMeta } from '../../lib/permissions/pageMeta'

export const pageMeta: PageMeta = {
  key: 'business_settings.users',
  label: 'Users',
  module: 'Business Settings',
  section: 'Team',
  description: 'Manage users and their roles',
  defaultPermissions: {
    owner: 'edit', admin: 'edit', manager: 'none',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

const LEGACY_ROLES = ['owner', 'admin', 'location_admin', 'manager', 'cashier', 'accountant', 'viewer'] as const
type LegacyRole = (typeof LEGACY_ROLES)[number]

const ROLE_BADGE: Record<string, string> = {
  owner: 'badge-orange', admin: 'badge-blue', location_admin: 'badge-blue',
  manager: 'badge-green', cashier: 'badge-gray',
  accountant: 'badge-green', viewer: 'badge-gray',
}

type UserRow = {
  id: string; user_id: string; role: string; role_id: string | null
  is_active: boolean; full_name: string | null; first_name: string | null
  last_name: string | null; email: string | null; username: string | null
  phone: string | null; created_at: string
  staff_id: string | null
  staff: { personnel_number: number; full_name: string } | null
}

function getDisplayName(u: UserRow): string {
  if (u.first_name || u.last_name) {
    return [u.first_name, u.last_name].filter(Boolean).join(' ')
  }
  if (u.full_name) return u.full_name
  if (u.email) return u.email.split('@')[0]
  return u.user_id.slice(0, 8) + '…'
}

function getAvatarLetter(u: UserRow): string {
  return getDisplayName(u).charAt(0).toUpperCase()
}

function toLegacyRole(roleName: string): LegacyRole {
  const lower = roleName.toLowerCase().replace(/\s+/g, '_')
  return LEGACY_ROLES.includes(lower as LegacyRole) ? (lower as LegacyRole) : 'viewer'
}

export default function UsersPage() {
  const { tenant, role: myRole, user } = useAuthStore()
  const tid = tenant?.id ?? ''
  const qc = useQueryClient()
  const { can } = usePermissions()
  const viewerIsLocationAdmin = myRole === 'location_admin'
  // location_admin is a location-scoped operational role (see canViewerManageUser);
  // it is not bypassed by can(), so it is kept explicitly to avoid regressing its
  // user-management access.
  const canManage = can('settings.users') || viewerIsLocationAdmin
  const canResetPassword = can('settings.users.reset_password')

  // Plan limits — gate the "add user" actions when the tenant is at its cap.
  const { canAddUser, usage, limits } = usePlanLimits()
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const guardAddUser = (open: () => void) => {
    if (!canAddUser) { setLimitModalOpen(true); return }
    open()
  }

  // Add user — manual vs from-staff
  const [addOpen, setAddOpen] = useState(false)
  const [addFromStaffOpen, setAddFromStaffOpen] = useState(false)
  const [newFullName, setNewFullName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newConfirmPassword, setNewConfirmPassword] = useState('')
  const [newRoleId, setNewRoleId] = useState('')
  const [newPwdShow, setNewPwdShow] = useState(false)
  const [newConfirmShow, setNewConfirmShow] = useState(false)
  // Location access for new (manual) user
  const [newAccessMode, setNewAccessMode] = useState<'all' | 'specific'>('all')
  const [newSelectedLocationIds, setNewSelectedLocationIds] = useState<string[]>([])
  const [newLocationError, setNewLocationError] = useState('')

  // Edit role form
  const [editUser, setEditUser] = useState<UserRow | null>(null)
  const [editRole, setEditRole] = useState<LegacyRole>('viewer')
  const [editRoleId, setEditRoleId] = useState('')

  // Location access form (inside Edit modal)
  const [accessMode, setAccessMode] = useState<'all' | 'specific'>('all')
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([])
  const [loadingCurrentLocations, setLoadingCurrentLocations] = useState(false)
  const [locationError, setLocationError] = useState('')

  // Reset password modal
  const [resetUser, setResetUser] = useState<UserRow | null>(null)
  const [resetPwd, setResetPwd] = useState('')
  const [resetConfirm, setResetConfirm] = useState('')
  const [resetPwdShow, setResetPwdShow] = useState(false)
  const [resetConfirmShow, setResetConfirmShow] = useState(false)

  // Remove user
  const [removeUser, setRemoveUser] = useState<UserRow | null>(null)

  const { data: users = [], isLoading } = useQuery<UserRow[]>({
    queryKey: ['tenant-users', tid],
    enabled: !!tid,
    queryFn: () => fetchTenantUsers(tid),
  })

  const { data: roles = [] } = useQuery({
    queryKey: ['roles', tid],
    enabled: !!tid,
    queryFn: () => fetchRoles(tid),
  })

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', tid],
    enabled: !!tid,
    queryFn: () => fetchWarehouses(tid),
  })

  const { data: assignmentsMap } = useUserLocationAssignments(tid)

  // Viewer's own allowed locations — only matters if viewer is location_admin
  const { data: viewerLocationIds } = useUserLocationIds()
  const viewerLocationSet = new Set(viewerLocationIds ?? [])

  function canViewerManageUser(u: UserRow): boolean {
    if (!viewerIsLocationAdmin) return true
    if (u.role === 'owner' || u.role === 'admin') return false
    const targetLocs = assignmentsMap?.get(u.user_id) ?? []
    if (targetLocs.length === 0) return false
    return targetLocs.every(l => viewerLocationSet.has(l.id))
  }

  // Effective legacy role currently selected in the edit form
  const editSelectedRole = roles.find(r => r.id === editRoleId)
  const editLegacyRole: LegacyRole = editSelectedRole ? toLegacyRole(editSelectedRole.name) : editRole
  const editIsPrivileged = editLegacyRole === 'owner' || editLegacyRole === 'admin'
  const editRequiresLocation = editLegacyRole === 'location_admin'

  // Load the target user's current locations when modal opens
  useEffect(() => {
    if (!editUser || !tid) return
    let cancelled = false
    setLocationError('')
    setLoadingCurrentLocations(true)
    ;(async () => {
      const { data } = await db('user_locations')
        .select('location_id')
        .eq('user_id', editUser.user_id)
        .eq('tenant_id', tid)
      if (cancelled) return
      const ids = ((data ?? []) as Array<{ location_id: string }>).map(r => r.location_id)
      setSelectedLocationIds(ids)
      setAccessMode(ids.length === 0 ? 'all' : 'specific')
      setLoadingCurrentLocations(false)
    })()
    return () => { cancelled = true }
  }, [editUser, tid])

  // Location Admin requires Specific mode — flip automatically when role changes
  useEffect(() => {
    if (editRequiresLocation && accessMode !== 'specific') {
      setAccessMode('specific')
    }
  }, [editRequiresLocation, accessMode])

  // Effective legacy role currently selected in the Add (manual) form
  const newSelectedRole = roles.find(r => r.id === newRoleId)
  const newLegacyRole: LegacyRole = newSelectedRole ? toLegacyRole(newSelectedRole.name) : 'viewer'
  const newIsPrivileged = newLegacyRole === 'owner' || newLegacyRole === 'admin'
  const newRequiresLocation = newLegacyRole === 'location_admin'

  // Auto-switch the Add form to Specific mode when role becomes location_admin
  useEffect(() => {
    if (newRequiresLocation && newAccessMode !== 'specific') {
      setNewAccessMode('specific')
    }
  }, [newRequiresLocation, newAccessMode])

  function resetAddForm() {
    setNewFullName(''); setNewEmail(''); setNewPassword('')
    setNewConfirmPassword(''); setNewRoleId('')
    setNewPwdShow(false); setNewConfirmShow(false)
    setNewAccessMode('all'); setNewSelectedLocationIds([]); setNewLocationError('')
  }

  const addUser = useMutation({
    mutationFn: async () => {
      if (!canManage) throw new Error('Not authorized to manage users')
      const email = newEmail.trim().toLowerCase()
      const fullName = newFullName.trim()
      const password = newPassword
      const confirmPassword = newConfirmPassword

      if (!fullName) throw new Error('Full name is required')
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Enter a valid email address')
      if (password.length < 8) throw new Error('Password must be at least 8 characters')
      if (password !== confirmPassword) throw new Error('Passwords do not match')

      const selectedRole = roles.find(r => r.id === newRoleId)
      if (!selectedRole && roles.length > 0) throw new Error('Please select a role')

      // Check email uniqueness within tenant
      const { data: existing } = await db('tenant_users')
        .select('id')
        .eq('tenant_id', tid)
        .eq('email', email)
        .maybeSingle()
      if (existing) throw new Error('This email is already in your team. Use a different email address.')

      const legacyRole = selectedRole ? toLegacyRole(selectedRole.name) : 'viewer'
      const requiresLocation = legacyRole === 'location_admin'
      const isPrivileged = legacyRole === 'owner' || legacyRole === 'admin'

      if (requiresLocation && newSelectedLocationIds.length === 0) {
        setNewLocationError('Location Admin role requires at least one location.')
        throw new Error('Location Admin role requires at least one location.')
      }
      if (!isPrivileged && !requiresLocation && newAccessMode === 'specific' && newSelectedLocationIds.length === 0) {
        setNewLocationError('Select at least one location or choose All.')
        throw new Error('Select at least one location or choose All.')
      }
      setNewLocationError('')

      const payloadLocationIds = isPrivileged
        ? []
        : (newAccessMode === 'specific' ? newSelectedLocationIds : [])

      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email,
          password,
          full_name: fullName,
          role: legacyRole,
          role_id: newRoleId || null,
          location_ids: payloadLocationIds,
        },
      })
      if (error) {
        const msg = (data as { error?: string } | null)?.error ?? error.message
        throw new Error(msg || 'Failed to create user')
      }
      if (data && typeof data === 'object' && 'error' in data && data.error) {
        throw new Error(String((data as { error: string }).error))
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-users'] })
      qc.invalidateQueries({ queryKey: ['user-location-assignments'] })
      setAddOpen(false)
      resetAddForm()
      toast.success(`${newFullName || newEmail} has been added to your team. They can now log in with their email and password.`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const saveEdit = useMutation({
    mutationFn: async () => {
      if (!canManage) throw new Error('Not authorized to manage users')
      if (!editUser) return { sessions_closed: 0, isPrivileged: true }
      const selectedRole = roles.find(r => r.id === editRoleId)
      const legacyRole = selectedRole ? toLegacyRole(selectedRole.name) : editRole
      const isPrivileged = legacyRole === 'owner' || legacyRole === 'admin'
      const requiresLocation = legacyRole === 'location_admin'

      if (requiresLocation && selectedLocationIds.length === 0) {
        setLocationError('Location Admin role requires at least one location.')
        throw new Error('Location Admin role requires at least one location.')
      }
      if (!isPrivileged && !requiresLocation && accessMode === 'specific' && selectedLocationIds.length === 0) {
        setLocationError('Select at least one location or choose All.')
        throw new Error('Select at least one location or choose All.')
      }
      setLocationError('')

      // Role changes go through the SECURITY DEFINER guard (owner/admin only, role
      // ceiling, no self-elevation, last-owner protection). Direct writes are denied
      // by RLS now (mig 395). The RPC's message (e.g. "only an owner may…") surfaces.
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any).rpc('set_tenant_member_role', {
          p_membership_id: editUser.id,
          p_role: legacyRole,
          p_role_id: editRoleId || null,
        })
        if (error) throw new Error(error.message)
      }

      if (isPrivileged) {
        return { sessions_closed: 0, isPrivileged: true }
      }

      const p_location_ids = requiresLocation
        ? selectedLocationIds
        : (accessMode === 'all' ? [] : selectedLocationIds)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('set_user_locations', {
        p_user_id: editUser.user_id,
        p_location_ids,
      })
      if (error) throw error
      const sessions_closed = (data as { sessions_closed?: number } | null)?.sessions_closed ?? 0
      return { sessions_closed, isPrivileged: false }
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['tenant-users'] })
      qc.invalidateQueries({ queryKey: ['user-location-assignments'] })
      qc.invalidateQueries({ queryKey: ['user-location-ids'] })
      setEditUser(null)
      const closed = result?.sessions_closed ?? 0
      if (result?.isPrivileged) {
        toast.success('Role updated')
      } else if (closed > 0) {
        toast.success(`Locations updated. ${closed} open POS session${closed === 1 ? '' : 's'} were force-closed.`)
      } else {
        toast.success('Locations updated')
      }
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const deactivate = useMutation({
    mutationFn: async (u: UserRow) => {
      if (!canManage) throw new Error('Not authorized to manage users')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('set_tenant_member_active', {
        p_membership_id: u.id,
        p_active: !u.is_active,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant-users'] }); toast.success('User updated') },
    onError: (e: Error) => toast.error(e.message),
  })

  const resetPassword = useMutation({
    mutationFn: async () => {
      if (!canResetPassword) throw new Error('Not authorized to reset passwords')
      if (!resetUser) return
      if (resetPwd.length < 8) throw new Error('Password must be at least 8 characters')
      if (resetPwd !== resetConfirm) throw new Error('Passwords do not match')
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: { user_id: resetUser.user_id, password: resetPwd },
      })
      if (error) {
        const msg = (data as { error?: string } | null)?.error ?? error.message
        throw new Error(msg || 'Failed to reset password')
      }
      if (data && typeof data === 'object' && 'error' in data && data.error) {
        throw new Error(String((data as { error: string }).error))
      }
    },
    onSuccess: () => {
      const name = resetUser ? getDisplayName(resetUser) : 'User'
      setResetUser(null)
      setResetPwd(''); setResetConfirm('')
      setResetPwdShow(false); setResetConfirmShow(false)
      toast.success(`Password reset successfully. Tell ${name} their new password.`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const removeFromTenant = useMutation({
    mutationFn: async (u: UserRow) => {
      if (!canManage) throw new Error('Not authorized to manage users')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc('remove_tenant_member', {
        p_membership_id: u.id,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-users'] })
      setRemoveUser(null)
      toast.success('User removed from team')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function openEdit(u: UserRow) {
    setEditUser(u)
    setEditRole(u.role as LegacyRole)
    setEditRoleId(u.role_id ?? '')
  }

  function openReset(u: UserRow) {
    setResetUser(u)
    setResetPwd(''); setResetConfirm('')
    setResetPwdShow(false); setResetConfirmShow(false)
  }

  if (!canManage) {
    return (
      <div className="space-y-4">
        <PageHeader title="Users & Permissions" subtitle="Manage team members" />
        <div className="card text-center py-12">
          <Shield className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Only owners and admins can manage users.</p>
        </div>
      </div>
    )
  }

  const activeCount = users.filter(u => u.is_active).length
  const usersSubtitle = limits?.max_users != null && limits.max_users !== -1
    ? `Users (${formatLimitLabel(usage?.users ?? activeCount, limits.max_users)})`
    : `${activeCount} active member${activeCount !== 1 ? 's' : ''}`

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users & Permissions"
        subtitle={usersSubtitle}
        actions={
          <div className="flex gap-2">
            <button className="btn-primary" onClick={() => guardAddUser(() => setAddFromStaffOpen(true))}>
              <UserPlus className="h-4 w-4" /> Add from Staff
            </button>
            <button className="btn-secondary" onClick={() => guardAddUser(() => setAddOpen(true))}>
              <Plus className="h-4 w-4" /> Add Manual User
            </button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : users.length === 0 ? (
        <EmptyState
          title="No team members yet"
          description="Invite your team to collaborate in Palstro."
          icon={<Shield className="h-8 w-8" />}
          action={
            <div className="flex gap-2 justify-center">
              <button className="btn-primary" onClick={() => guardAddUser(() => setAddFromStaffOpen(true))}>
                <UserPlus className="h-4 w-4" /> Add from Staff
              </button>
              <button className="btn-secondary" onClick={() => guardAddUser(() => setAddOpen(true))}>
                <Plus className="h-4 w-4" /> Add Manual User
              </button>
            </div>
          }
        />
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 hidden md:table-cell">Locations</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => {
                const displayName = getDisplayName(u)
                const roleName = roles.find(r => r.id === u.role_id)?.name ?? u.role
                const isMe = user?.id === u.user_id
                const canManageThisUser = canViewerManageUser(u)
                const outOfScopeTitle = !canManageThisUser ? 'Outside your location scope' : ''
                return (
                  <tr key={u.id} className={!u.is_active ? 'opacity-50' : ''}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-xs flex-shrink-0">
                          {getAvatarLetter(u)}
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900 truncate max-w-[160px] block">{displayName}</span>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            {isMe && <span className="text-[10px] text-brand-500 font-medium">You</span>}
                            {u.role === 'owner' ? (
                              <span className="inline-flex items-center rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                Owner
                              </span>
                            ) : u.staff_id ? (
                              <Link
                                to={`/payroll/staff/${u.staff_id}`}
                                className="inline-flex items-center rounded-full bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 hover:bg-brand-200 transition-colors"
                                title="View staff record"
                              >
                                From Staff #{u.staff?.personnel_number ?? '—'}
                              </Link>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                                Manual
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[160px] hidden md:table-cell">
                      {u.email ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${ROLE_BADGE[u.role] ?? 'badge-gray'}`}>{roleName}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {(() => {
                        const isPrivileged = u.role === 'owner' || u.role === 'admin'
                        if (isPrivileged) {
                          return <span className="badge badge-gray">All locations</span>
                        }
                        const list = assignmentsMap?.get(u.user_id) ?? []
                        if (list.length === 0) {
                          return <span className="badge badge-blue">All locations</span>
                        }
                        if (list.length === 1) {
                          return <span className="text-xs text-gray-700">{list[0].name}</span>
                        }
                        return (
                          <span
                            className="text-xs text-gray-700 underline decoration-dotted cursor-help"
                            title={list.map(l => l.name).join('\n')}
                          >
                            {list.length} locations
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${u.is_active ? 'badge-green' : 'badge-red'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button title={outOfScopeTitle || 'Edit User'}
                          disabled={!canManageThisUser}
                          onClick={() => openEdit(u)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400">
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        {canResetPassword && u.user_id !== '00000000-0000-0000-0000-000000000000' && (
                          <button title="Reset Password" onClick={() => openReset(u)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors">
                            <Key className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {!isMe && (
                          <button title={outOfScopeTitle || (u.is_active ? 'Deactivate' : 'Activate')}
                            disabled={!canManageThisUser}
                            onClick={() => deactivate.mutate(u)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-amber-500 hover:bg-amber-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400">
                            <UserX className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {!isMe && (
                          <button title={outOfScopeTitle || 'Remove from team'}
                            disabled={!canManageThisUser}
                            onClick={() => setRemoveUser(u)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add from Staff Modal */}
      <AddUserFromStaffModal
        open={addFromStaffOpen}
        onClose={() => setAddFromStaffOpen(false)}
        tenantId={tid}
        roles={roles}
      />

      {/* Add User Modal — manual */}
      <Modal open={addOpen} onClose={() => { setAddOpen(false); resetAddForm() }} title="Add Team Member" size="sm">
        <div className="space-y-4">
          <div>
            <label className="label">Full Name *</label>
            <input autoFocus className="input" value={newFullName}
              onChange={e => setNewFullName(e.target.value)} placeholder="John Doe" />
          </div>
          <div>
            <label className="label">Email Address *</label>
            <input className="input" type="email" value={newEmail}
              onChange={e => setNewEmail(e.target.value)} placeholder="john@example.com" />
          </div>
          <div>
            <label className="label">Password *</label>
            <div className="relative">
              <input className="input pr-10" type={newPwdShow ? 'text' : 'password'}
                value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="Min. 8 characters" />
              <button type="button" onClick={() => setNewPwdShow(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {newPwdShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">Confirm Password *</label>
            <div className="relative">
              <input className="input pr-10" type={newConfirmShow ? 'text' : 'password'}
                value={newConfirmPassword} onChange={e => setNewConfirmPassword(e.target.value)}
                placeholder="Repeat password" />
              <button type="button" onClick={() => setNewConfirmShow(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {newConfirmShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">Role *</label>
            {roles.length > 0 ? (
              <select className="input" value={newRoleId} onChange={e => setNewRoleId(e.target.value)}>
                <option value="">— Select a role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}{r.description ? ` — ${r.description}` : ''}</option>)}
              </select>
            ) : (
              <select className="input" value={newRoleId} onChange={e => setNewRoleId(e.target.value)}>
                <option value="">— Select a role —</option>
                {LEGACY_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>

          {/* Location Access section */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">Location Access</h3>
            </div>

            {newIsPrivileged ? (
              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
                Owners and Admins always have access to all locations.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className={`flex items-center gap-2 text-sm ${newRequiresLocation ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="new-access-mode"
                      checked={newAccessMode === 'all'}
                      disabled={newRequiresLocation}
                      onChange={() => { setNewAccessMode('all'); setNewLocationError('') }}
                    />
                    <span>All locations</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="new-access-mode"
                      checked={newAccessMode === 'specific'}
                      onChange={() => setNewAccessMode('specific')}
                    />
                    <span>Specific locations</span>
                  </label>
                </div>

                {newAccessMode === 'specific' && (
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {warehouses.filter(w => w.is_active !== false).length === 0 ? (
                      <p className="text-xs text-gray-500">No locations available.</p>
                    ) : (
                      warehouses.filter(w => w.is_active !== false).map(w => {
                        const checked = newSelectedLocationIds.includes(w.id)
                        return (
                          <label key={w.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                setNewLocationError('')
                                setNewSelectedLocationIds(prev =>
                                  e.target.checked ? [...prev, w.id] : prev.filter(id => id !== w.id)
                                )
                              }}
                            />
                            <span>{w.name}</span>
                          </label>
                        )
                      })
                    )}
                  </div>
                )}

                {newLocationError && (
                  <p className="text-xs text-red-600">{newLocationError}</p>
                )}

                <p className="text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2">
                  {newRequiresLocation
                    ? 'Location Admin role requires at least one location.'
                    : 'At least one location must be selected if restricting access.'}
                </p>
              </div>
            )}
          </div>

          <button className="btn-primary w-full" disabled={addUser.isPending} onClick={() => addUser.mutate()}>
            {addUser.isPending ? <Spinner size="sm" /> : 'Add User'}
          </button>
        </div>
      </Modal>

      {/* Edit Role Modal */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Edit User" size="sm">
        <div className="space-y-4">
          {editUser && (
            <p className="text-sm text-gray-600">
              Editing <strong>{getDisplayName(editUser)}</strong>
            </p>
          )}
          <div>
            <label className="label">Role</label>
            {roles.length > 0 ? (
              <select className="input" value={editRoleId} onChange={e => setEditRoleId(e.target.value)}>
                <option value="">— Select a role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            ) : (
              <select className="input" value={editRole} onChange={e => setEditRole(e.target.value as LegacyRole)}>
                {LEGACY_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>

          {/* Location Access section */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">Location Access</h3>
            </div>

            {editIsPrivileged ? (
              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
                Owners and Admins always have access to all locations.
              </p>
            ) : loadingCurrentLocations ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Spinner size="sm" /> Loading current access…
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className={`flex items-center gap-2 text-sm ${editRequiresLocation ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="access-mode"
                      checked={accessMode === 'all'}
                      disabled={editRequiresLocation}
                      onChange={() => { setAccessMode('all'); setLocationError('') }}
                    />
                    <span>All locations</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="access-mode"
                      checked={accessMode === 'specific'}
                      onChange={() => setAccessMode('specific')}
                    />
                    <span>Specific locations</span>
                  </label>
                </div>

                {accessMode === 'specific' && (
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {warehouses.filter(w => w.is_active !== false).length === 0 ? (
                      <p className="text-xs text-gray-500">No locations available.</p>
                    ) : (
                      warehouses.filter(w => w.is_active !== false).map(w => {
                        const checked = selectedLocationIds.includes(w.id)
                        return (
                          <label key={w.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                setLocationError('')
                                setSelectedLocationIds(prev =>
                                  e.target.checked ? [...prev, w.id] : prev.filter(id => id !== w.id)
                                )
                              }}
                            />
                            <span>{w.name}</span>
                          </label>
                        )
                      })
                    )}
                  </div>
                )}

                {locationError && (
                  <p className="text-xs text-red-600">{locationError}</p>
                )}

                <p className="text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2">
                  {editRequiresLocation
                    ? 'Location Admin role requires at least one location.'
                    : 'At least one location must be selected if restricting access.'}
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  Any open POS sessions at unselected locations will be force-closed.
                </p>
              </div>
            )}
          </div>

          <button className="btn-primary w-full" disabled={saveEdit.isPending || loadingCurrentLocations} onClick={() => saveEdit.mutate()}>
            {saveEdit.isPending ? <Spinner size="sm" /> : <><Check className="h-4 w-4" /> Save Changes</>}
          </button>
        </div>
      </Modal>

      {/* Reset Password Modal (owner only) */}
      <Modal open={!!resetUser} onClose={() => { setResetUser(null); setResetPwd(''); setResetConfirm('') }} title={`Reset Password — ${resetUser ? getDisplayName(resetUser) : ''}`} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">Set a new password for this user. Make sure to share it with them securely.</p>
          <div>
            <label className="label">New Password *</label>
            <div className="relative">
              <input autoFocus className="input pr-10" type={resetPwdShow ? 'text' : 'password'}
                value={resetPwd} onChange={e => setResetPwd(e.target.value)}
                placeholder="Min. 8 characters" />
              <button type="button" onClick={() => setResetPwdShow(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {resetPwdShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="label">Confirm Password *</label>
            <div className="relative">
              <input className="input pr-10" type={resetConfirmShow ? 'text' : 'password'}
                value={resetConfirm} onChange={e => setResetConfirm(e.target.value)}
                placeholder="Repeat password" />
              <button type="button" onClick={() => setResetConfirmShow(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {resetConfirmShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <button className="btn-primary w-full" disabled={resetPassword.isPending} onClick={() => resetPassword.mutate()}>
            {resetPassword.isPending ? <Spinner size="sm" /> : 'Reset Password'}
          </button>
        </div>
      </Modal>

      {/* Remove user confirm */}
      <Modal open={!!removeUser} onClose={() => setRemoveUser(null)} title="Remove Team Member" size="sm">
        <p className="text-sm text-gray-600 mb-4">
          Remove <strong>{removeUser ? getDisplayName(removeUser) : ''}</strong> from your team?
          They will lose access to this workspace. Their Supabase account will remain active.
        </p>
        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={() => setRemoveUser(null)}>Cancel</button>
          <button className="btn-danger flex-1" disabled={removeFromTenant.isPending}
            onClick={() => removeUser && removeFromTenant.mutate(removeUser)}>
            {removeFromTenant.isPending ? <Spinner size="sm" /> : 'Remove'}
          </button>
        </div>
      </Modal>

      {/* Plan limit reached */}
      {limitModalOpen && (
        <UpgradeLimitModal
          resource="users"
          current={usage?.users ?? activeCount}
          limit={limits?.max_users ?? 0}
          onClose={() => setLimitModalOpen(false)}
        />
      )}
    </div>
  )
}
